import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ResolveIcon from '@mui/icons-material/esm/Check';
import DiscardIcon from '@mui/icons-material/esm/Close';
import RefreshIcon from '@mui/icons-material/esm/Refresh';
import PageHeader from '../../components/layout/PageHeader';
import DataTable, { Column } from '../../components/common/DataTable';
import syncService, { SyncConflictReviewItem } from '../../services/api/syncService';

const formatDate = (value?: string | null) => value ? new Date(value).toLocaleString() : '-';

const summarizePayload = (payload: Record<string, any> | null) => {
  if (!payload) return '-';
  const entries = Object.entries(payload).filter(([, value]) => value !== undefined && value !== null);
  if (entries.length === 0) return '-';
  return entries.slice(0, 4).map(([key, value]) => `${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`).join(' | ');
};

export default function SyncConflictsPage() {
  const [rows, setRows] = useState<SyncConflictReviewItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SyncConflictReviewItem | null>(null);
  const [decision, setDecision] = useState<'resolve' | 'discard' | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setRows(await syncService.listConflicts({ status: 'open' }));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Unable to load sync conflicts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const columns: Column<SyncConflictReviewItem>[] = useMemo(() => [
    { id: 'entityType', label: 'Entity', minWidth: 120, format: (value) => <Chip size="small" label={value} /> },
    { id: 'entityId', label: 'Record', minWidth: 160 },
    { id: 'createdAt', label: 'Queued', minWidth: 150, format: (value) => formatDate(value) },
    { id: 'serverVersionAt', label: 'Server version', minWidth: 150, format: (value) => formatDate(value) },
    { id: 'clientPayload', label: 'Client change', minWidth: 260, format: (_, row) => summarizePayload(row.clientPayload) },
    { id: 'serverPayload', label: 'Current server', minWidth: 260, format: (_, row) => summarizePayload(row.serverPayload) },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 180,
      sortable: false,
      format: (_, row) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            startIcon={<ResolveIcon />}
            onClick={(event) => {
              event.stopPropagation();
              setSelected(row);
              setDecision('resolve');
              setNotes('');
            }}
          >
            Apply
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<DiscardIcon />}
            onClick={(event) => {
              event.stopPropagation();
              setSelected(row);
              setDecision('discard');
              setNotes('');
            }}
          >
            Discard
          </Button>
        </Stack>
      ),
    },
  ], []);

  const submitDecision = async () => {
    if (!selected || !decision) return;
    try {
      setSaving(true);
      if (decision === 'resolve') {
        await syncService.resolveConflict(selected.id, notes);
      } else {
        await syncService.discardConflict(selected.id, notes);
      }
      setSelected(null);
      setDecision(null);
      setNotes('');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Unable to update sync conflict');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Sync Conflicts"
        subtitle="Review offline edits that arrived after the server record changed."
        actions={
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Chip label={`${rows.length} open`} color={rows.length ? 'warning' : 'success'} />
          <Chip label="Apply uses client value" variant="outlined" />
          <Chip label="Discard keeps server value" variant="outlined" />
        </Stack>
      </Paper>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        searchable
        searchPlaceholder="Search conflicts"
        defaultRowsPerPage={10}
      />

      <Dialog open={Boolean(selected && decision)} onClose={() => !saving && setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>{decision === 'resolve' ? 'Apply client change' : 'Discard client change'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {selected?.entityType} / {selected?.entityId}
            </Typography>
            <TextField
              label="Notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc' }}>
              <Typography variant="subtitle2" gutterBottom>Client change</Typography>
              <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}>
                {JSON.stringify(selected?.clientPayload ?? {}, null, 2)}
              </Box>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc' }}>
              <Typography variant="subtitle2" gutterBottom>Current server</Typography>
              <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}>
                {JSON.stringify(selected?.serverPayload ?? {}, null, 2)}
              </Box>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            color={decision === 'resolve' ? 'primary' : 'inherit'}
            onClick={submitDecision}
            disabled={saving}
          >
            {decision === 'resolve' ? 'Apply Client' : 'Discard Client'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
